import React from 'react';
import { clsx } from 'clsx';
// Import all the icons you plan to use from lucide-react here
import {
  // Navigation & Chevrons
  ChevronLeft, ChevronRight, ChevronDown, ChevronUp, ArrowLeft, ArrowRight, ArrowUp, ArrowDown, ArrowUpRight, MoveUp, MoveDown,

  // Actions
  Heart, Star, CheckCircle2, XCircle, Check, X, Search, Plus, Minus, Settings, SlidersHorizontal, Share2, Trash2, Download, Upload, UploadCloud, Link, Mail, Bell, Clock, Edit3, Paperclip, Send, LogOut, RefreshCcw, RefreshCw, MoreVertical, Shield, Power, ThumbsUp, ThumbsDown,

  // Objects & Items
  Diamond, User, Users, Gem, Sparkles, MessageSquare, Files, Folder, Calendar, Image, Bookmark, MapPin, Gift, Camera, Briefcase, Award, BarChart2, PieChart, CreditCard, Droplet, Headphones, Home, Map, Pause, Play, ShoppingBag, ShoppingCart, Tag, Tv, Watch, Wind, Hammer,

  // Status & Info
  AlertCircle, AlertTriangle, Info, Eye, EyeOff, Lock, Unlock, Sun, Moon, Zap, Wifi, WifiOff, Volume2, VolumeX, BarChart, TrendingUp, TrendingDown, GitCommit, GitPullRequest, GitMerge, List, Grid, LayoutGrid,

  // Special/Misc
  Snowflake, Fingerprint, Globe, GripVertical, Waves, Rss, PoundSterling
} from 'lucide-react';

// Custom components for brand icons
import { InfoGreyIcon, SortIcon, FilterIcon, GoogleIcon, AppleIcon, PinterestIcon, PinterestRedIcon, PinterestWhiteIcon, MyRingsIcon, InfoIconGuide, LocationIcon, PlayIconGradient, CompanyLogoIcon, CompanyLogoWhiteIcon } from './BrandIcons';

import styles from './Icon.module.css';

// This is the mapping from the simple `name` prop to the actual component.
// Add any new icons you need to this map.
const iconMap = {
  // Chevrons & Arrows
  'chevron-left': ChevronLeft,
  'chevron-right': ChevronRight,
  'chevron-down': ChevronDown,
  'chevron-up': ChevronUp,
  'arrow-left': ArrowLeft,
  'arrow-right': ArrowRight,
  'arrow-up': ArrowUp,
  'arrow-down': ArrowDown,
  'arrow-up-right': ArrowUpRight,

  // Common Actions
  'heart': Heart,
  'heart-filled': (props) => <Heart {...props} fill="currentColor" />,
  'star': Star,
  'star-filled': (props) => <Star {...props} fill="currentColor" />,
  'star-half': (props) => <Star {...props} fill="currentColor" />, // Lucide doesn't have half-star, this is a visual stand-in
  'check-circle': CheckCircle2,
  'check-circle-filled': (props) => <CheckCircle2 {...props} fill="currentColor" />,
  'check': Check,
  'close': X,
  'x': X,
  'close-circle': XCircle,
  'search': Search,
  'plus': Plus,
  'minus': Minus,
  'settings': Settings,
  'sliders': SlidersHorizontal,
  'share': Share2,
  'trash': Trash2,
  'download': Download,
  'upload': Upload,
  'upload-cloud': UploadCloud,
  'link': Link,
  'mail': Mail,
  'bell': Bell,
  'clock': Clock,
  'edit': Edit3,
  'paperclip': Paperclip,
  'send': Send,
  'logout': LogOut,
  'refresh': RefreshCcw,
  'refresh-ccw': RefreshCcw,
  'refresh-cw': RefreshCw,
  'rotate-cw': RefreshCw,
  'more': MoreVertical,
  'shield': Shield,
  'power': Power,
  'pound-sterling': PoundSterling,
  'thumbs-up': ThumbsUp,
  'thumbs-down': ThumbsDown,

  // Common Objects
  'diamond': Diamond,
  'user': User,
  'users': Users,
  'gem': Gem,
  'sparkles': Sparkles,
  'message': MessageSquare,
  'message-circle': MessageSquare,
  'files': Files,
  'folder': Folder,
  'calendar': Calendar,
  'image': Image,
  'bookmark': Bookmark,
  'pin': MapPin,
  'gift': Gift,
  'camera': Camera,
  'briefcase': Briefcase,
  'award': Award,
  'credit-card': CreditCard,
  'droplet': Droplet,
  'headphones': Headphones,
  'home': Home,
  'map': Map,
  'pause': Pause,
  'play': Play,
  'bag': ShoppingBag,
  'cart': ShoppingCart,
  'tag': Tag,
  'tv': Tv,
  'watch': Watch,
  'hammer': Hammer,

  // Status & Info
  'info': Info,
  'alert-triangle': AlertTriangle,
  'warning': AlertTriangle,
  'alert-circle': AlertCircle,
  'eye': Eye,
  'eye-off': EyeOff,
  'lock': Lock,
  'unlock': Unlock,
  'sun': Sun,
  'moon': Moon,
  'zap': Zap,
  'wifi': Wifi,
  'wifi-off': WifiOff,
  'volume': Volume2,
  'volume-off': VolumeX,
  'trending-up': TrendingUp,
  'trending-down': TrendingDown,
  'list': List,
  'grid': Grid,
  'logout': LogOut,

  // Misc
  'fingerprint': Fingerprint,
  'globe': Globe,
  'snowflake': Snowflake,

  // Brand Icons
  'pinterest': PinterestIcon,
  'pinterest-red': PinterestRedIcon,
  'pinterest-white': PinterestWhiteIcon,
  'apple': AppleIcon,
  'google': GoogleIcon,
  'my-rings': MyRingsIcon,
  'info-guide': InfoIconGuide,
  'location': LocationIcon,
  'info-grey': InfoGreyIcon,
  'sort': SortIcon,
  'filter': FilterIcon,
  'play-gradient': PlayIconGradient,
  'company-logo': CompanyLogoIcon,
  'company-logo-white': CompanyLogoWhiteIcon,
};

// Map your design system sizes to pixel values
const sizeMap = {
  xs: 12,
  s: 16,
  m: 20, // Default size
  l: 24,
  xl: 32,
};

/**
 * A centralized Icon component for the entire application.
 *
 * @param {object} props
 * @param {keyof iconMap} props.name - The name of the icon to display.
 * @param {'xs'|'s'|'m'|'l'|'xl'} [props.size='m'] - The size of the icon from the design system.
 * @param {string} [props.className] - Optional additional classnames.
 * @param {number} [props.size] - Optional size override as a number
 */
const Icon = ({ name, size = 'm', className, ...rest }) => {
  const IconComponent = iconMap[name];

  if (!IconComponent) {
    console.warn(`Icon "${name}" not found in the iconMap.`);
    // You could return a placeholder icon here if you wanted
    return null;
  }

  // Handle both design system size strings and direct number values
  const iconSize = typeof size === 'number' ? size : (sizeMap[size] || sizeMap.m);

  const iconClasses = clsx(styles.icon, className);

  return <IconComponent className={iconClasses} size={iconSize} {...rest} />;
};

export default Icon;
